import { useEffect, useRef, useState, type MouseEvent, type ReactNode } from "react";

export interface FloatingReportTocItem {
  id: string;
  label: string;
  meta?: string;
}

interface FloatingReportLayoutProps {
  items: FloatingReportTocItem[];
  children: ReactNode;
  label?: string;
  showNumbers?: boolean;
  tone?: "brand" | "cool";
}

export function FloatingReportLayout({
  items,
  children,
  label = "报告目录",
  showNumbers = true,
  tone = "brand",
}: FloatingReportLayoutProps): JSX.Element {
  const [activeId, setActiveId] = useState(items[0]?.id ?? "");
  const targetLockRef = useRef<string>();
  const targetLockUserNavigationRef = useRef(false);

  const handleTocClick = (event: MouseEvent<HTMLAnchorElement>, itemId: string) => {
    event.preventDefault();
    const target = document.getElementById(itemId);
    if (!target) return;

    const scrollRoot = document.getElementById("main-content");
    targetLockRef.current = itemId;
    targetLockUserNavigationRef.current = false;
    setActiveId(itemId);
    window.history.replaceState(window.history.state, "", `#${itemId}`);
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!scrollRoot) {
      target.scrollIntoView({ behavior: reducedMotion ? "auto" : "smooth", block: "start" });
      return;
    }
    const rootRect = scrollRoot.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    scrollRoot.scrollTo({
      top: scrollRoot.scrollTop + targetRect.top - rootRect.top - 16,
      behavior: reducedMotion ? "auto" : "smooth",
    });
  };

  useEffect(() => {
    const scrollRoot = document.getElementById("main-content");
    const sections = items
      .map((item) => document.getElementById(item.id))
      .filter((section): section is HTMLElement => section !== null);

    if (sections.length === 0) {
      return undefined;
    }

    let frame = 0;
    const updateActiveSection = () => {
      const rootTop = scrollRoot?.getBoundingClientRect().top ?? 0;
      const rootHeight = scrollRoot?.clientHeight ?? window.innerHeight;
      const readingLine = rootTop + Math.min(220, rootHeight * 0.25);
      const lockedId = targetLockRef.current;
      if (lockedId) {
        if (!targetLockUserNavigationRef.current) {
          setActiveId(lockedId);
          return;
        }
        const targetIndex = sections.findIndex((section) => section.id === lockedId);
        const target = sections[targetIndex];
        const nextTarget = sections[targetIndex + 1];
        const targetRect = target?.getBoundingClientRect();
        const targetIsVisible = Boolean(
          targetRect && targetRect.bottom > rootTop && targetRect.top < rootTop + rootHeight,
        );
        if (
          targetIsVisible &&
          (!nextTarget || nextTarget.getBoundingClientRect().top > readingLine)
        ) {
          setActiveId(lockedId);
          return;
        }
        targetLockRef.current = undefined;
        targetLockUserNavigationRef.current = false;
      }
      let nextActiveId = sections[0].id;

      for (const section of sections) {
        if (section.getBoundingClientRect().top <= readingLine) {
          nextActiveId = section.id;
        } else {
          break;
        }
      }

      setActiveId((current) => (current === nextActiveId ? current : nextActiveId));
    };

    const scheduleUpdate = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(updateActiveSection);
    };

    const markUserNavigation = () => {
      if (targetLockRef.current) {
        targetLockUserNavigationRef.current = true;
      }
      scheduleUpdate();
    };

    const handleNavigationKey = (event: KeyboardEvent) => {
      if (["ArrowDown", "ArrowUp", "PageDown", "PageUp", "Home", "End", " "].includes(event.key)) {
        markUserNavigation();
      }
    };

    setActiveId((current) =>
      sections.some((section) => section.id === current) ? current : sections[0].id,
    );
    updateActiveSection();
    scrollRoot?.addEventListener("scroll", scheduleUpdate, { passive: true });
    scrollRoot?.addEventListener("wheel", markUserNavigation, { passive: true });
    scrollRoot?.addEventListener("touchstart", markUserNavigation, { passive: true });
    window.addEventListener("keydown", handleNavigationKey);
    window.addEventListener("resize", scheduleUpdate);

    return () => {
      window.cancelAnimationFrame(frame);
      scrollRoot?.removeEventListener("scroll", scheduleUpdate);
      scrollRoot?.removeEventListener("wheel", markUserNavigation);
      scrollRoot?.removeEventListener("touchstart", markUserNavigation);
      window.removeEventListener("keydown", handleNavigationKey);
      window.removeEventListener("resize", scheduleUpdate);
    };
  }, [items]);

  return (
    <div className="grid gap-5 print:block lg:grid-cols-[16rem_minmax(0,1fr)] lg:items-start">
      <aside className="min-w-0 rounded-2xl border border-slate-200 bg-white/95 p-3 shadow-sm backdrop-blur print:hidden lg:sticky lg:top-4 lg:max-h-[calc(100dvh-7rem)] lg:overflow-y-auto">
        <p className="px-2 pb-2 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
          {label}
        </p>
        <nav aria-label={label}>
          <ol className="flex gap-2 overflow-x-auto pb-1 lg:flex-col lg:overflow-visible lg:pb-0">
            {items.map((item, index) => {
              const isActive = activeId === item.id;
              const activeClassName =
                tone === "cool"
                  ? "border-violet-200 bg-accent-cool-soft text-accent-cool shadow-sm"
                  : "border-brand/25 bg-brand/10 text-brand-dark shadow-sm";
              const inactiveClassName =
                tone === "cool"
                  ? "border-transparent bg-slate-50 text-slate-600 hover:border-violet-200 hover:bg-accent-cool-soft/50 hover:text-accent-cool"
                  : "border-transparent bg-slate-50 text-slate-600 hover:border-brand/20 hover:bg-brand/5 hover:text-brand-dark";
              return (
                <li key={item.id} className="shrink-0 lg:w-full">
                  <a
                    href={`#${item.id}`}
                    aria-current={isActive ? "location" : undefined}
                    onClick={(event) => handleTocClick(event, item.id)}
                    className={`flex min-h-11 items-center gap-3 rounded-xl border px-3 py-2 text-sm font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 ${
                      isActive ? activeClassName : inactiveClassName
                    }`}
                  >
                    {showNumbers ? (
                      <span
                        aria-hidden="true"
                        className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
                          isActive
                            ? tone === "cool"
                              ? "bg-accent-cool text-white"
                              : "bg-brand text-white"
                            : "bg-white text-slate-500"
                        }`}
                      >
                        {index + 1}
                      </span>
                    ) : null}
                    <span className="min-w-0">
                      <span className="block whitespace-nowrap font-semibold lg:whitespace-normal">
                        {item.label}
                      </span>
                      {item.meta ? (
                        <span className="mt-1 block text-xs font-normal">{item.meta}</span>
                      ) : null}
                    </span>
                  </a>
                </li>
              );
            })}
          </ol>
        </nav>
      </aside>
      <div className="min-w-0 space-y-6">{children}</div>
    </div>
  );
}
